import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { SettingsState } from '../../../model/admin/settingsState';
import { DailyMetricCardVisibility } from '../../../model/config/DailyMetricCardVisibility';
import { DailyParameterCardVisibility } from '../../../model/config/DailyParameterCardVisibility';
import { OtherCardVisibility } from '../../../model/config/OtherCardVisibility';
import {
  saveUserSettings,
  updateUserSetting,
} from '../../../redux/actions/settingsActions';
import { store, useAppThunkDispatch } from '../../../redux/configureStore';
import { selectAccountId } from '../../../redux/selectors/accountSelectors';
import {
  environmentDailyMetricsVisibilitySettings,
  environmentDailyParametersVisibilitySettings,
  environmentOtherVisibilitySettings,
  userDailyMetricsVisibilitySettings,
  userDailyParametersVisibilitySettings,
  userOtherVisibilitySettings,
} from '../../../redux/selectors/settingsSelectors';

const formatLabel = (key: string) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const dispatch = useAppThunkDispatch();
  const accountId = useSelector(selectAccountId);
  const userDailyMetricsVisibility = useSelector(
    userDailyMetricsVisibilitySettings
  );
  const userDailyParametersVisibility = useSelector(
    userDailyParametersVisibilitySettings
  );
  const userOtherVisibility = useSelector(userOtherVisibilitySettings);

  // Get environment settings to determine constraints
  const envDailyMetricsVisibility = useSelector(
    environmentDailyMetricsVisibilitySettings
  );
  const envDailyParametersVisibility = useSelector(
    environmentDailyParametersVisibilitySettings
  );
  const envOtherVisibility = useSelector(environmentOtherVisibilitySettings);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    category: keyof SettingsState,
    key: string
  ) => {
    dispatch(updateUserSetting(category, key, event.target.checked))
      .then(() => {
        //I dont like this
        const state = store.getState();
        let value;

        switch (category) {
          case 'DailyMetricsVisibility':
            value = state.settings.user.DailyMetricsVisibility;
            break;
          case 'DailyParametersVisibility':
            value = state.settings.user.DailyParametersVisibility;
            break;
          case 'OtherVisibility':
            value = state.settings.user.OtherVisibility;
            break;
          default:
            throw new Error(`Unknown category: ${category}`);
        }

        dispatch(saveUserSettings(accountId, category, value));
      })
      .catch((error) => {
        console.error('Error updating user setting:', error);
      });
  };

  // Get the corresponding environment settings for a given category
  const getEnvironmentSettings = (
    category: keyof SettingsState
  ): Record<string, boolean> => {
    switch (category) {
      case 'DailyMetricsVisibility':
        return envDailyMetricsVisibility as unknown as Record<string, boolean>;
      case 'DailyParametersVisibility':
        return envDailyParametersVisibility as unknown as Record<
          string,
          boolean
        >;
      case 'OtherVisibility':
        return envOtherVisibility as unknown as Record<string, boolean>;
      default:
        return {};
    }
  };

  // Type that represents all possible visibility settings
  type VisibilitySettings =
    | DailyMetricCardVisibility
    | DailyParameterCardVisibility
    | OtherCardVisibility;

  const renderCheckboxes = (
    visibility: Record<string, boolean>,
    category: keyof SettingsState
  ) => {
    const envSettings = getEnvironmentSettings(category);

    return (
      <Grid container spacing={2} wrap="wrap" sx={{ alignItems: 'center' }}>
        {Object.keys(visibility).map((key) => {
          // If environment setting is false, don't render the checkbox at all
          if (!envSettings[key]) {
            return null;
          }

          return (
            <Grid key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibility[key]}
                    onChange={(event) => handleChange(event, category, key)}
                    name={key}
                  />
                }
                label={t(`cardLabels.${key}`, { defaultValue: formatLabel(key) })}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginRight: 30 }}>
      <Typography variant="h4" component="h4">
        {t('title')}
      </Typography>
      <Typography sx={{ mt: 2 }}>{t('intro')}</Typography>
      <Typography sx={{ mt: 2 }} variant="h5" component="h5">
        {t('cardVisibility')}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={12}>
          <Typography variant="h6">{t('groups.dailyMetrics')}</Typography>
          {renderCheckboxes(
            userDailyMetricsVisibility as unknown as Record<string, boolean>,
            'DailyMetricsVisibility' as keyof SettingsState
          )}
        </Grid>
        <Grid size={12}>
          <Typography variant="h6">{t('groups.dailyParameters')}</Typography>
          {renderCheckboxes(
            userDailyParametersVisibility as unknown as Record<string, boolean>,
            'DailyParametersVisibility' as keyof SettingsState
          )}
        </Grid>
        <Grid size={12}>
          <Typography variant="h6">{t('groups.other')}</Typography>
          {renderCheckboxes(
            userOtherVisibility as unknown as Record<string, boolean>,
            'OtherVisibility' as keyof SettingsState
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;
